import { Category } from '../types';
import { CURRENT_E8_VERSION, QuestionnaireType } from './versions';

/**
 * Essential Eight Categories with their questions
 */
export const ESSENTIAL_EIGHT_CATEGORIES: Category[] = [
  {
    id: 'patch-applications',
    title: 'Patch Applications',
    questions: [
      {
        id: 'patch-applications-a',
        text: 'Do you use an up-to-date automated tool at least fortnightly to find all your computers and software; scan for missing updates in your office suite, web browsers (and add-ons), email software, PDF software, and security products; and then apply important fixes quickly (within 48 hours for urgent issues in online services, within two weeks for other updates)? Have you removed any of programs or online services if the vendor no longer supports them?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'patch-applications-b',
        text: 'Do you scan all your other business software for missing updates at least once a fortnight, apply updates within one month?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'patch-applications-c',
        text: 'For all your business software, do you apply critical updates within 48 hours and other updates within two weeks, scan for missing updates at least once a fortnight, and ensure no unsupported software is kept anywhere in the business?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'patch-operating-systems',
    title: 'Patch Operating Systems',
    questions: [
      {
        id: 'patch-operating-systems-a',
        text: 'Do you use an up-to-date automated tool at least fortnightly to find all your computers, servers, and network devices; scan for missing operating system updates; and then apply important fixes quickly (within 48 hours for urgent issues on internet-facing systems, within two weeks for other internet-facing updates, and within one month for all other systems)? Have you replaced any operating systems that are no longer supported by the vendor?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'patch-operating-systems-b',
        text: 'Do you also run vulnerability scans on internet-facing servers and network devices daily, and on all other systems at least once a fortnight, to check for missing updates; and then apply important fixes quickly (within 48 hours)?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'patch-operating-systems-c',
        text: 'Do you also run vulnerability scans for drivers and firmware (not just the operating system) on a regular schedule, with critical drivers and firmware applied within 48 hours and other fixes within 1 month?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'mfa',
    title: 'Multi-Factor Authentication (MFA)',
    questions: [
      {
        id: 'mfa-a',
        text: 'Do staff use MFA when logging into your cloud/online services and important third-party services that hold your sensitive data (and, where relevant, your customers use MFA for your customer-facing portals)?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'mfa-b',
        text: 'Is MFA required for all users of your systems and workstations (both admin and non-admin), and is MFA for cloud logins phishing-resistant (eg. passkeys/security keys), with MFA sign-in events centrally logged?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'mfa-c',
        text: 'Is MFA also enforced for data repositories (file shares and data platforms) and customer logins in a phishing-resistant way, with security logs reviewed across servers and workstations?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'restrict-admin',
    title: 'Restrict Administrative Privileges',
    questions: [
      {
        id: 'restrict-admin-a',
        text: 'Do admins have a separate admin account (not used for email/web), and are admin accounts blocked from browsing and email except where strictly required?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'restrict-admin-b',
        text: 'Do you review admin access at least annually (removing access if not re-approved), disable unused admin access after 45 days, use jump servers for admin tasks, and centrally log admin and privileged events?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'restrict-admin-c',
        text: 'Do admins use hardened admin-only computers (Secure Admin Workstations), is just-in-time admin access used, and are advanced Windows protections (eg. Credential Guard/LSA protection) enabled, while monitoring logs across servers and workstations?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'application-control',
    title: 'Application Control',
    questions: [
      {
        id: 'application-control-a',
        text: 'On staff computers, do you block programs and scripts from running unless they\'re on an approved list, especially blocking anything from Downloads, email or temporary folders?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'application-control-b',
        text: 'Is application control also on your internet-facing servers, do you use Microsoft\'s recommended blocklist, review rules at least annually, and log allow and deny events?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'application-control-c',
        text: 'Is application control enforced on all servers (including non-internet-facing), does it block unapproved drivers and use Microsoft\'s vulnerable driver blocklist, with logs reviewed across the environment?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'office-macros',
    title: 'Restrict Microsoft Office Macros',
    questions: [
      {
        id: 'office-macros-a',
        text: 'Are Office macros disabled for people who don\'t need them, blocked if the file came from the internet, scanned by antivirus, and are macro security settings locked so users can\'t change them?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'office-macros-b',
        text: 'Are macros also blocked from making Win32 API calls (a common attack technique)?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'office-macros-c',
        text: 'Do you allow macros to run only if they\'re in a sandbox or trusted location or digitally signed by a trusted publisher (after being checked clean), with the trusted list reviewed annually?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'app-hardening',
    title: 'User Application Hardening',
    questions: [
      {
        id: 'app-hardening-a',
        text: 'Is Internet Explorer 11 removed/disabled and do browsers block Java and web ads?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'app-hardening-b',
        text: 'Are browsers and Office hardened to vendor and ASD guidance, with Office blocked from creating child processes and executables and injecting code, and similar hardening applied to PDF software?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'app-hardening-c',
        text: 'Have you removed .NET 3.5 and PowerShell 2.0, set PowerShell to Constrained Language Mode, and do you centrally log PowerShell and command-line events and review logs across servers and workstations?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  },
  {
    id: 'backups',
    title: 'Regular Backups',
    questions: [
      {
        id: 'backups-a',
        text: 'Do you back up data, apps and settings on a schedule that matches business needs, keep backups retained and secure, test restores, and stop standard users from accessing and modifying backups?',
        part: 'A',
        maturityLevel: 1
      },
      {
        id: 'backups-b',
        text: 'Are privileged (admin) accounts also blocked from accessing or modifying backups (except backup admins)?',
        part: 'B',
        maturityLevel: 2
      },
      {
        id: 'backups-c',
        text: 'Are no users (even themselves) allowed to access or modify their own backups, and are backup admins prevented from modifying or deleting backups during the retention period?',
        part: 'C',
        maturityLevel: 3
      }
    ]
  }
];

/**
 * Current questionnaire with version information
 */
export const ESSENTIAL_EIGHT_QUESTIONNAIRE = {
  type: QuestionnaireType.ESSENTIAL_EIGHT,
  version: CURRENT_E8_VERSION,
  categories: ESSENTIAL_EIGHT_CATEGORIES
};
